import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../theme';
import { saviezVousData, SaviezVousItem } from '../data/saviezVous';

const DidYouKnowSection = styled.section`
  width: 100%;
  max-width: 100%;
  padding: 80px 5%;
  background: ${theme.colors.outchou_blue};
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 60px 4%;
  }

  @media (max-width: 480px) {
    padding: 40px 3%;
  }
`;

const DidYouKnowContent = styled.div`
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  overflow: hidden;

  @media (max-width: 480px) {
    width: 100%;
    max-width: 100%;
    padding: 0;
  }
`;

const DidYouKnowTitle = styled.h2`
  font-family: ${theme.fonts.title};
  font-weight: 700;
  font-size: 36px;
  line-height: 44px;
  color: ${theme.colors.outchou_yellow};
  margin: 0 0 40px 0;

  @media (max-width: 768px) {
    font-size: 28px;
    line-height: 36px;
    margin-bottom: 32px;
  }

  @media (max-width: 480px) {
    font-size: 24px;
    line-height: 32px;
    margin-bottom: 24px;
  }
`;

const SliderContainer = styled.div`
  width: 100%;
  max-width: 100%;
  position: relative;
  overflow: hidden;
  min-height: 80px;

  @media (max-width: 480px) {
    width: 100%;
    max-width: 100%;
    overflow: hidden;
    min-height: 60px;
  }
`;

const CarouselControls = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  display: flex;
  justify-content: space-between;
  pointer-events: none;
  z-index: 10;
  padding: 0 24px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const CarouselButton = styled.button`
  width: 48px;
  height: 48px;
  /* Rond totalement transparent */
  background: transparent;
  border: none;
  border-radius: 50%;
  box-shadow: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  pointer-events: auto;

  &:hover {
    background: transparent;
    transform: scale(1.1);
  }
`;

const ArrowIcon = styled.span<{ $direction: 'left' | 'right' }>`
  width: 20px;
  height: 20px;
  display: inline-block;

  /* Même dégradé que le texte (strong) */
  background: linear-gradient(
    135deg,
    ${theme.colors.outchou_yellow} 0%,
    ${theme.colors.outchou_green} 50%,
    #7DD3A0 100%
  );

  /* On utilise l'icône PNG comme "mask" pour remplir avec le dégradé */
  -webkit-mask-image: url(${props =>
    props.$direction === 'left'
      ? '/shared_assets/images/carousel_left.png'
      : '/shared_assets/images/carousel_right.png'});
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center;
  -webkit-mask-size: contain;

  mask-image: url(${props =>
    props.$direction === 'left'
      ? '/shared_assets/images/carousel_left.png'
      : '/shared_assets/images/carousel_right.png'});
  mask-repeat: no-repeat;
  mask-position: center;
  mask-size: contain;
`;

const SliderTrack = styled.div<{ $currentSlide: number; $totalSlides: number; $isMobile: boolean }>`
  display: flex;
  position: absolute;
  top: 0;
  left: 0;
  transition: transform 0.5s ease-in-out;
  width: ${props => `${props.$totalSlides * 100}%`};
  height: 100%;
  transform: translateX(${props => `calc(-${props.$currentSlide} * (100% / ${props.$totalSlides}))`});
`;

const SlideGroup = styled.div<{ $isMobile: boolean; $totalSlides: number }>`
  display: flex;
  flex: 0 0 ${props => `calc(100% / ${props.$totalSlides})`};
  width: ${props => `calc(100% / ${props.$totalSlides})`};
  min-width: ${props => `calc(100% / ${props.$totalSlides})`};
  max-width: ${props => `calc(100% / ${props.$totalSlides})`};
  gap: 0;
  padding: 0 4%;
  box-sizing: border-box;
  white-space: normal;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const DidYouKnowCard = styled.div<{ $isMobile: boolean }>`
  background: transparent;
  box-shadow: none;
  padding: 0;
  min-height: auto;
  width: 100%;
  max-width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  box-sizing: border-box;
  overflow: visible;
  white-space: normal;
`;

const DidYouKnowText = styled.p`
  font-family: ${theme.fonts.body};
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: ${theme.colors.outchou_yellow};
  margin: 0;
  text-align: center;
  width: 100%;
  max-width: 100%;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  white-space: normal;
  overflow: visible;
  box-sizing: border-box;

  strong {
    font-weight: 700;
    background: linear-gradient(135deg, ${theme.colors.outchou_yellow} 0%, ${theme.colors.outchou_green} 50%, #7DD3A0 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  @media (max-width: 768px) {
    font-size: 15px;
    line-height: 22px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
    line-height: 20px;
  }
`;

const PaginationDots = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 32px;

  @media (max-width: 768px) {
    margin-top: 24px;
    gap: 10px;
  }

  @media (max-width: 480px) {
    margin-top: 20px;
    gap: 8px;
  }
`;

const Dot = styled.button<{ $active: boolean }>`
  width: ${props => props.$active ? '12px' : '10px'};
  height: ${props => props.$active ? '10px' : '10px'};
  border-radius: 50%;
  border: 2px solid ${theme.colors.outchou_yellow};
  background: ${props => props.$active ? theme.colors.outchou_yellow : 'transparent'};
  cursor: pointer;
  padding: 0;
  transition: all 0.3s ease;
  outline: none;

  &:hover {
    transform: scale(1.2);
    background: ${props => props.$active ? theme.colors.outchou_yellow : 'rgba(255, 193, 7, 0.3)'};
  }

  &:focus {
    outline: 2px solid ${theme.colors.outchou_yellow};
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    width: ${props => props.$active ? '10px' : '8px'};
    height: ${props => props.$active ? '10px' : '8px'};
    border-width: 1.5px;
  }

  @media (max-width: 480px) {
    width: ${props => props.$active ? '8px' : '6px'};
    height: ${props => props.$active ? '8px' : '6px'};
    border-width: 1px;
  }
`;

const DidYouKnowSlider: React.FC = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // Détecter si on est sur mobile (breakpoint 768px comme le carrousel logo)
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Une phrase = un slide (même mise en page sur web et mobile)
  const totalSlides = saviezVousData.length;

  // Minuteur auto (comme le hero): change de slide automatiquement.
  // Le timer se réinitialise à chaque changement de slide (clic, swipe, flèches, dots).
  useEffect(() => {
    if (totalSlides <= 1) return;
    const timer = window.setTimeout(() => {
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
    }, 8000);

    return () => window.clearTimeout(timer);
  }, [currentSlide, totalSlides]);

  const handleDotClick = (slideIndex: number) => {
    setCurrentSlide(slideIndex);
  };

  const goPrev = () => {
    setCurrentSlide((prev) => (prev === 0 ? totalSlides - 1 : prev - 1));
  };

  const goNext = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };

  // Gestion du swipe sur mobile
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    if (!isMobile) return;
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (!isMobile) return;
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!isMobile || !touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      // Swipe gauche : slide suivant
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
    } else if (isRightSwipe) {
      // Swipe droite : slide précédent
      setCurrentSlide((prev) => (prev === 0 ? totalSlides - 1 : prev - 1));
    }
  };

  return (
    <DidYouKnowSection
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <DidYouKnowContent>
        <DidYouKnowTitle>Le saviez-vous ?</DidYouKnowTitle>
        <SliderContainer>
          <SliderTrack $currentSlide={currentSlide} $totalSlides={totalSlides} $isMobile={isMobile}>
            {saviezVousData.map((item) => (
              <SlideGroup key={item.id} $isMobile={isMobile} $totalSlides={totalSlides}>
                <DidYouKnowCard $isMobile={isMobile}>
                  <DidYouKnowText dangerouslySetInnerHTML={{ __html: item.text }} />
                </DidYouKnowCard>
              </SlideGroup>
            ))}
          </SliderTrack>
          <CarouselControls>
            <CarouselButton onClick={goPrev} aria-label="Slide précédente">
              <ArrowIcon $direction="left" />
            </CarouselButton>
            <CarouselButton onClick={goNext} aria-label="Slide suivante">
              <ArrowIcon $direction="right" />
            </CarouselButton>
          </CarouselControls>
        </SliderContainer>
        <PaginationDots>
          {Array.from({ length: totalSlides }).map((_, index) => (
            <Dot
              key={index}
              $active={index === currentSlide}
              onClick={() => handleDotClick(index)}
              aria-label={`Aller à la slide ${index + 1}`}
            />
          ))}
        </PaginationDots>
      </DidYouKnowContent>
    </DidYouKnowSection>
  );
};

export default DidYouKnowSlider;

